import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Grid,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  TablePagination,
  Collapse,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import PersonIcon from '@mui/icons-material/Person';
import HistoryIcon from '@mui/icons-material/History';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://pawtel-app.onrender.com';

interface DiscountCode {
  _id: string;
  code: string;
  providerId: string;
  providerName: string;
  discountPercent: number;
  appliesTo: 'all_services' | 'specific_services';
  serviceIds?: string[];
  maxUses: number | null;
  maxUsesPerCustomer: number;
  currentUses: number;
  expiresAt: string | null;
  status: 'pending' | 'approved' | 'rejected' | 'expired' | 'disabled';
  statusReason?: string;
  statusUpdatedAt?: string;
  statusUpdatedBy?: string;
  createdAt: string;
}

interface ActivityItem {
  _id: string;
  action: string;
  reason?: string;
  performedByName?: string;
  createdAt: string;
}

interface CodeDetail {
  discountCode: DiscountCode;
  activity: ActivityItem[];
  usages: any[];
}

interface DiscountCodeStats {
  pending: number;
  approved: number;
  rejected: number;
  disabled: number;
  total: number;
}

interface Props {
  onClose: () => void;
}

function getToken(): string | null {
  return localStorage.getItem('admin_token');
}

async function fetchWithAuth(endpoint: string, options: RequestInit = {}) {
  const token = getToken();
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || `HTTP ${response.status}`);
  }

  return response.json();
}

function getStatusColor(status: string): 'warning' | 'success' | 'error' | 'default' | 'info' {
  switch (status) {
    case 'pending': return 'warning';
    case 'approved': return 'success';
    case 'rejected': return 'error';
    case 'disabled': return 'info';
    case 'expired':
    default: return 'default';
  }
}

function getStatusLabel(status: string): string {
  switch (status) {
    case 'disabled': return 'Paused';
    default: return status.charAt(0).toUpperCase() + status.slice(1);
  }
}

function getActionColor(action: string): string {
  switch (action) {
    case 'approved':
    case 'enabled': return '#2e7d32';
    case 'rejected':
    case 'deleted': return '#d32f2f';
    case 'paused': return '#ed6c02';
    default: return '#757575';
  }
}

function getActionDotColor(action: string): 'success' | 'error' | 'warning' | 'grey' {
  switch (action) {
    case 'approved':
    case 'enabled': return 'success';
    case 'rejected':
    case 'deleted': return 'error';
    case 'paused': return 'warning';
    default: return 'grey';
  }
}

function formatDate(dateString: string | null): string {
  if (!dateString) return 'Never';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatDateTime(dateString: string): string {
  return new Date(dateString).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

// ============================================================
// Expandable Row Component
// ============================================================
function ExpandableRow({
  code,
  actionLoading,
  onApprove,
  onReject,
  onDisable,
  onEnable,
  onDelete,
  onEdit,
}: {
  code: DiscountCode;
  actionLoading: string | null;
  onApprove: (id: string) => void;
  onReject: (id: string) => void;
  onDisable: (id: string) => void;
  onEnable: (id: string) => void;
  onDelete: (id: string) => void;
  onEdit: (code: DiscountCode) => void;
}) {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState<CodeDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const handleToggle = async () => {
    const willOpen = !open;
    setOpen(willOpen);

    if (willOpen && !detail) {
      setDetailLoading(true);
      try {
        const data = await fetchWithAuth(`/api/admin/discount-codes/${code._id}/detail`);
        setDetail(data);
      } catch (err) {
        console.error('Failed to load code detail:', err);
      } finally {
        setDetailLoading(false);
      }
    }
  };

  const isExpired = code.expiresAt && new Date(code.expiresAt) <= new Date();
  const isMaxedOut = code.maxUses !== null && code.currentUses >= code.maxUses;

  return (
    <>
      <TableRow
        hover
        sx={{ cursor: 'pointer', '& > *': { borderBottom: open ? 'none' : undefined } }}
        onClick={handleToggle}
      >
        <TableCell sx={{ width: 40, pr: 0 }}>
          <IconButton size="small">
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Typography variant="body2" sx={{ fontWeight: 'bold', fontFamily: 'monospace', fontSize: '0.9rem' }}>
            {code.code}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2">{code.providerName}</Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2" fontWeight="bold">{code.discountPercent}%</Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2">
            {code.currentUses}{code.maxUses != null ? ` / ${code.maxUses}` : ''}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2">{formatDate(code.expiresAt)}</Typography>
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            <Chip
              label={getStatusLabel(code.status)}
              color={getStatusColor(code.status)}
              size="small"
            />
            {isExpired && code.status === 'approved' && (
              <Chip label="Expired" size="small" color="default" variant="outlined" />
            )}
            {isMaxedOut && (
              <Chip label="Maxed" size="small" color="default" variant="outlined" />
            )}
          </Box>
        </TableCell>
        <TableCell onClick={(e) => e.stopPropagation()}>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {code.status === 'pending' && (
              <>
                <Tooltip title="Approve">
                  <IconButton
                    color="success"
                    size="small"
                    disabled={actionLoading === code._id}
                    onClick={() => onApprove(code._id)}
                  >
                    {actionLoading === code._id ? <CircularProgress size={18} /> : <CheckCircleIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Reject">
                  <IconButton
                    color="error"
                    size="small"
                    disabled={actionLoading === code._id}
                    onClick={() => onReject(code._id)}
                  >
                    <CancelIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
            {code.status === 'approved' && (
              <Tooltip title="Pause">
                <IconButton
                  color="warning"
                  size="small"
                  disabled={actionLoading === code._id}
                  onClick={() => onDisable(code._id)}
                >
                  {actionLoading === code._id ? <CircularProgress size={18} /> : <PauseCircleIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            )}
            {code.status === 'disabled' && (
              <Tooltip title="Re-enable">
                <IconButton
                  color="success"
                  size="small"
                  disabled={actionLoading === code._id}
                  onClick={() => onEnable(code._id)}
                >
                  {actionLoading === code._id ? <CircularProgress size={18} /> : <PlayCircleIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Edit discount %">
              <IconButton
                color="primary"
                size="small"
                disabled={actionLoading === code._id}
                onClick={() => onEdit(code)}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                color="error"
                size="small"
                disabled={actionLoading === code._id}
                onClick={() => onDelete(code._id)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </TableCell>
      </TableRow>

      {/* Expanded Detail Row */}
      <TableRow>
        <TableCell colSpan={8} sx={{ py: 0, borderBottom: open ? undefined : 'none' }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ py: 3, px: 2 }}>
              {detailLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : (
                <Grid container spacing={3}>
                  {/* Left: Code Details */}
                  <Grid item xs={12} md={5}>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <InfoOutlinedIcon fontSize="small" /> Code Details
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Grid container spacing={1.5}>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">Code</Typography>
                          <Typography variant="body2" fontWeight="bold" sx={{ fontFamily: 'monospace', fontSize: '1rem' }}>
                            {code.code}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">Status</Typography>
                          <Box>
                            <Chip
                              label={getStatusLabel(code.status)}
                              color={getStatusColor(code.status)}
                              size="small"
                            />
                          </Box>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">Discount</Typography>
                          <Typography variant="body2" fontWeight="bold">{code.discountPercent}% off</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">Applies To</Typography>
                          <Typography variant="body2">
                            {code.appliesTo === 'all_services' ? 'All Services' : `Specific (${code.serviceIds?.length || 0})`}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">Provider</Typography>
                          <Typography variant="body2">{code.providerName}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">Created</Typography>
                          <Typography variant="body2">{formatDateTime(code.createdAt)}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">Times Used</Typography>
                          <Typography variant="body2" fontWeight="bold">
                            {code.currentUses}{code.maxUses != null ? ` / ${code.maxUses}` : ' (unlimited)'}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">Max Uses Per Customer</Typography>
                          <Typography variant="body2">{code.maxUsesPerCustomer || 1}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">Expires</Typography>
                          <Typography variant="body2">
                            {code.expiresAt ? formatDateTime(code.expiresAt) : 'Never'}
                          </Typography>
                        </Grid>
                        {code.statusReason && (
                          <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary">Rejection Reason</Typography>
                            <Typography variant="body2" color="error.main" sx={{ fontStyle: 'italic' }}>
                              {code.statusReason}
                            </Typography>
                          </Grid>
                        )}
                      </Grid>
                    </Paper>

                    {/* Usage Section */}
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 3, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PersonIcon fontSize="small" /> Usage by Customers
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      {detail?.usages && detail.usages.length > 0 ? (
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell><strong>Customer</strong></TableCell>
                              <TableCell><strong>Date</strong></TableCell>
                              <TableCell align="right"><strong>Amount</strong></TableCell>
                              <TableCell align="right"><strong>Saved</strong></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {detail.usages.map((usage: any, i: number) => (
                              <TableRow key={i}>
                                <TableCell>{usage.customerName || 'Unknown'}</TableCell>
                                <TableCell>{formatDate(usage.usedAt)}</TableCell>
                                <TableCell align="right">{usage.currency || '$'}{((usage.originalAmount || 0) / 100).toFixed(2)}</TableCell>
                                <TableCell align="right" sx={{ color: '#2e7d32', fontWeight: 'bold' }}>
                                  -{usage.currency || '$'}{((usage.discountAmount || 0) / 100).toFixed(2)}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center', py: 2 }}>
                          {code.currentUses > 0
                            ? `Used ${code.currentUses} time(s) — detailed tracking coming soon.`
                            : 'No usage yet.'}
                        </Typography>
                      )}
                    </Paper>
                  </Grid>

                  {/* Right: Activity Timeline */}
                  <Grid item xs={12} md={7}>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <HistoryIcon fontSize="small" /> Activity History
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 2, maxHeight: 400, overflowY: 'auto' }}>
                      {detail?.activity && detail.activity.length > 0 ? (
                        <Box sx={{ pl: 1 }}>
                          {detail.activity.map((item, index) => (
                            <Box
                              key={item._id}
                              sx={{
                                display: 'flex',
                                gap: 2,
                                mb: index < detail.activity.length - 1 ? 2.5 : 0,
                                position: 'relative',
                              }}
                            >
                              {/* Timeline dot and connector */}
                              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 20 }}>
                                <Box
                                  sx={{
                                    width: 12,
                                    height: 12,
                                    borderRadius: '50%',
                                    bgcolor: getActionColor(item.action),
                                    mt: 0.5,
                                    flexShrink: 0,
                                  }}
                                />
                                {index < detail.activity.length - 1 && (
                                  <Box
                                    sx={{
                                      width: 2,
                                      flex: 1,
                                      bgcolor: '#e0e0e0',
                                      mt: 0.5,
                                    }}
                                  />
                                )}
                              </Box>

                              {/* Content */}
                              <Box sx={{ flex: 1, pb: 0.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                  <Chip
                                    label={item.action.charAt(0).toUpperCase() + item.action.slice(1)}
                                    size="small"
                                    sx={{
                                      bgcolor: getActionColor(item.action),
                                      color: '#fff',
                                      fontWeight: 'bold',
                                      fontSize: '0.7rem',
                                      height: 22,
                                    }}
                                  />
                                  <Typography variant="caption" color="text.secondary">
                                    {formatDateTime(item.createdAt)}
                                  </Typography>
                                </Box>
                                <Typography variant="body2" color="text.secondary">
                                  {item.performedByName ? (
                                    <>by <strong>{item.performedByName}</strong></>
                                  ) : (
                                    'by System'
                                  )}
                                </Typography>
                                {item.reason && (
                                  <Typography variant="body2" sx={{ mt: 0.5, fontStyle: 'italic', color: '#666' }}>
                                    Reason: {item.reason}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center', py: 2 }}>
                          No activity recorded yet.
                        </Typography>
                      )}
                    </Paper>
                  </Grid>
                </Grid>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

// ============================================================
// Main Component
// ============================================================
export default function DiscountCodeManager({ onClose }: Props) {
  const [tabValue, setTabValue] = useState(0);
  const [codes, setCodes] = useState<DiscountCode[]>([]);
  const [stats, setStats] = useState<DiscountCodeStats>({ pending: 0, approved: 0, rejected: 0, disabled: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Reject dialog
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectCodeId, setRejectCodeId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  // Edit dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editCode, setEditCode] = useState<DiscountCode | null>(null);
  const [editPercent, setEditPercent] = useState('');
  const [editError, setEditError] = useState<string | null>(null);

  const TAB_STATUS_MAP: Record<number, string> = {
    0: 'pending',
    1: 'approved',
    2: 'disabled',
    3: 'rejected',
    4: '',
  };

  useEffect(() => {
    loadStats();
  }, []);

  useEffect(() => {
    setPage(0);
    loadCodes();
  }, [tabValue]);

  useEffect(() => {
    loadCodes();
  }, [page, rowsPerPage]);

  async function loadStats() {
    try {
      const data = await fetchWithAuth('/api/admin/discount-codes/stats');
      setStats({
        pending: data.pending || 0,
        approved: data.approved || 0,
        rejected: data.rejected || 0,
        disabled: data.disabled || 0,
        total: (data.pending || 0) + (data.approved || 0) + (data.rejected || 0) + (data.expired || 0) + (data.disabled || 0),
      });
    } catch (err: any) {
      console.error('Failed to load discount code stats:', err);
    }
  }

  async function loadCodes() {
    setLoading(true);
    setError(null);

    try {
      const status = TAB_STATUS_MAP[tabValue];
      const statusParam = status ? `&status=${status}` : '';
      const data = await fetchWithAuth(
        `/api/admin/discount-codes?page=${page + 1}&limit=${rowsPerPage}${statusParam}`
      );
      setCodes(data.discountCodes || []);
      setTotalCount(data.total || data.discountCodes?.length || 0);
    } catch (err: any) {
      setError(err.message || 'Failed to load discount codes');
      setCodes([]);
    } finally {
      setLoading(false);
    }
  }

  async function handleApprove(codeId: string) {
    setActionLoading(codeId);
    try {
      await fetchWithAuth(`/api/admin/discount-codes/${codeId}/approve`, { method: 'PUT' });
      await Promise.all([loadCodes(), loadStats()]);
    } catch (err: any) {
      setError(err.message || 'Failed to approve discount code');
    } finally {
      setActionLoading(null);
    }
  }

  function openRejectDialog(codeId: string) {
    setRejectCodeId(codeId);
    setRejectReason('');
    setRejectDialogOpen(true);
  }

  async function handleReject() {
    if (!rejectCodeId || !rejectReason.trim()) return;

    setActionLoading(rejectCodeId);
    setRejectDialogOpen(false);
    try {
      await fetchWithAuth(`/api/admin/discount-codes/${rejectCodeId}/reject`, {
        method: 'PUT',
        body: JSON.stringify({ reason: rejectReason.trim() }),
      });
      await Promise.all([loadCodes(), loadStats()]);
    } catch (err: any) {
      setError(err.message || 'Failed to reject discount code');
    } finally {
      setActionLoading(null);
      setRejectCodeId(null);
      setRejectReason('');
    }
  }

  async function handleDisable(codeId: string) {
    setActionLoading(codeId);
    try {
      await fetchWithAuth(`/api/admin/discount-codes/${codeId}/disable`, { method: 'PUT' });
      await Promise.all([loadCodes(), loadStats()]);
    } catch (err: any) {
      setError(err.message || 'Failed to pause discount code');
    } finally {
      setActionLoading(null);
    }
  }

  async function handleEnable(codeId: string) {
    setActionLoading(codeId);
    try {
      await fetchWithAuth(`/api/admin/discount-codes/${codeId}/enable`, { method: 'PUT' });
      await Promise.all([loadCodes(), loadStats()]);
    } catch (err: any) {
      setError(err.message || 'Failed to enable discount code');
    } finally {
      setActionLoading(null);
    }
  }

  function openEditDialog(code: DiscountCode) {
    setEditCode(code);
    setEditPercent(String(code.discountPercent));
    setEditError(null);
    setEditDialogOpen(true);
  }

  async function handleEditSave() {
    if (!editCode) return;

    const trimmed = editPercent.trim();
    if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) {
      setEditError('Enter a number with up to 2 decimal places (e.g. 12.5 or 7.25)');
      return;
    }

    const parsed = Number(trimmed);
    if (!Number.isFinite(parsed) || parsed < 0.01 || parsed > 30) {
      setEditError('Discount percent must be between 0.01 and 30');
      return;
    }

    setEditError(null);
    setActionLoading(editCode._id);
    try {
      await fetchWithAuth(`/api/admin/discount-codes/${editCode._id}`, {
        method: 'PUT',
        body: JSON.stringify({ discountPercent: parsed }),
      });
      setEditDialogOpen(false);
      setEditCode(null);
      setEditPercent('');
      await loadCodes();
    } catch (err: any) {
      setEditError(err.message || 'Failed to update discount code');
    } finally {
      setActionLoading(null);
    }
  }

  async function handleDelete(codeId: string) {
    if (!window.confirm('Are you sure you want to permanently delete this discount code?')) return;
    setActionLoading(codeId);
    try {
      await fetchWithAuth(`/api/admin/discount-codes/${codeId}`, { method: 'DELETE' });
      await Promise.all([loadCodes(), loadStats()]);
    } catch (err: any) {
      setError(err.message || 'Failed to delete discount code');
    } finally {
      setActionLoading(null);
    }
  }

  function handleRefresh() {
    loadCodes();
    loadStats();
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LocalOfferIcon sx={{ fontSize: 28 }} />
          <Typography variant="h5" fontWeight="bold">
            Discount Code Management
          </Typography>
        </Box>
        <Box>
          <Tooltip title="Refresh data">
            <IconButton onClick={handleRefresh}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Close">
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3} md={2.4}>
          <Paper
            sx={{
              p: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              cursor: 'pointer',
              border: tabValue === 0 ? '2px solid #ed6c02' : '2px solid transparent',
            }}
            onClick={() => setTabValue(0)}
          >
            <HourglassEmptyIcon sx={{ fontSize: 32, color: '#ed6c02' }} />
            <Box>
              <Typography variant="h5" fontWeight="bold">{stats.pending}</Typography>
              <Typography variant="caption" color="text.secondary">Pending</Typography>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3} md={2.4}>
          <Paper
            sx={{
              p: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              cursor: 'pointer',
              border: tabValue === 1 ? '2px solid #2e7d32' : '2px solid transparent',
            }}
            onClick={() => setTabValue(1)}
          >
            <CheckCircleIcon sx={{ fontSize: 32, color: '#2e7d32' }} />
            <Box>
              <Typography variant="h5" fontWeight="bold">{stats.approved}</Typography>
              <Typography variant="caption" color="text.secondary">Active</Typography>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3} md={2.4}>
          <Paper
            sx={{
              p: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              cursor: 'pointer',
              border: tabValue === 2 ? '2px solid #1976d2' : '2px solid transparent',
            }}
            onClick={() => setTabValue(2)}
          >
            <PauseCircleIcon sx={{ fontSize: 32, color: '#1976d2' }} />
            <Box>
              <Typography variant="h5" fontWeight="bold">{stats.disabled}</Typography>
              <Typography variant="caption" color="text.secondary">Paused</Typography>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3} md={2.4}>
          <Paper
            sx={{
              p: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              cursor: 'pointer',
              border: tabValue === 3 ? '2px solid #d32f2f' : '2px solid transparent',
            }}
            onClick={() => setTabValue(3)}
          >
            <CancelIcon sx={{ fontSize: 32, color: '#d32f2f' }} />
            <Box>
              <Typography variant="h5" fontWeight="bold">{stats.rejected}</Typography>
              <Typography variant="caption" color="text.secondary">Rejected</Typography>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3} md={2.4}>
          <Paper
            sx={{
              p: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              cursor: 'pointer',
              border: tabValue === 4 ? '2px solid #333' : '2px solid transparent',
            }}
            onClick={() => setTabValue(4)}
          >
            <LocalOfferIcon sx={{ fontSize: 32, color: '#333' }} />
            <Box>
              <Typography variant="h5" fontWeight="bold">{stats.total}</Typography>
              <Typography variant="caption" color="text.secondary">Total</Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#fafafa' }}>
                <TableCell sx={{ width: 40 }} />
                <TableCell><strong>Code</strong></TableCell>
                <TableCell><strong>Provider</strong></TableCell>
                <TableCell><strong>Discount</strong></TableCell>
                <TableCell><strong>Uses</strong></TableCell>
                <TableCell><strong>Expires</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {codes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No discount codes found.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                codes.map((code) => (
                  <ExpandableRow
                    key={code._id}
                    code={code}
                    actionLoading={actionLoading}
                    onApprove={handleApprove}
                    onReject={openRejectDialog}
                    onDisable={handleDisable}
                    onEnable={handleEnable}
                    onDelete={handleDelete}
                    onEdit={openEditDialog}
                  />
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25]}
          />
        </TableContainer>
      )}

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => {
          if (actionLoading !== editCode?._id) setEditDialogOpen(false);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Edit Discount Percent</DialogTitle>
        <DialogContent>
          {editCode && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Code <strong style={{ fontFamily: 'monospace' }}>{editCode.code}</strong> — current: {editCode.discountPercent}%
            </Typography>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="Discount Percent"
            type="number"
            fullWidth
            value={editPercent}
            onChange={(e) => setEditPercent(e.target.value)}
            inputProps={{ step: 0.01, min: 0.01, max: 30 }}
            helperText="Between 0.01 and 30, up to 2 decimal places"
            error={!!editError}
          />
          {editError && (
            <Alert severity="error" sx={{ mt: 2 }}>{editError}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setEditDialogOpen(false)}
            disabled={actionLoading === editCode?._id}
          >
            Cancel
          </Button>
          <Button
            onClick={handleEditSave}
            color="primary"
            variant="contained"
            disabled={!editPercent.trim() || actionLoading === editCode?._id}
          >
            {actionLoading === editCode?._id ? <CircularProgress size={18} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Discount Code</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Rejection Reason"
            fullWidth
            multiline
            rows={3}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            required
            placeholder="Provide a reason for rejecting this discount code..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleReject}
            color="error"
            variant="contained"
            disabled={!rejectReason.trim()}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
